'use client';

import { useMemo } from 'react';
import { useInstitutionTypes } from '@/lib/services/institutions/hooks';
import { InlineCreateSelectField } from './inline-create-step';
import {
  toInstitutionTypeOptions,
  type InstitutionTypeSide
} from './institution-type-options';

/**
 * **The institution-type choice on the create step** (VALE-1577).
 *
 * A component rather than a hook call inside `InstitutionSearchField`, for one
 * concrete reason: `useInstitutionTypes` is a React Query hook, and a hook cannot
 * be called conditionally. Called in the picker's own body it would run for every
 * one of the ~20 pickers that mount it — including in the many existing tests that
 * render them with no `QueryClientProvider`, where React Query does not degrade
 * but throws *"No QueryClient set"* and takes the whole render down. Scoping the
 * fetch to a child that only mounts on the `Other` picker's create step keeps the
 * hook off every other path by construction rather than by an `enabled` flag,
 * which would not have helped: `useQuery` needs the client to exist either way.
 *
 * It also means the request happens when the reader opens the step, not when the
 * field mounts — the endpoint reads a notification template per type server-side.
 * The result is shared reference data on a 30-minute `staleTime`, so a reader who
 * mints two organisations in a row pays for one request.
 *
 * **`side` scopes the offered types to the item being minted** (VALE-1595): an
 * asset picker must not offer a liability-only type (Lender, Tax Authority,
 * Funeral Service, Utility Provider, Professional Service) and a liability picker
 * must not offer an asset-only one. See `toInstitutionTypeOptions`.
 */
export function InstitutionTypeSelect({
  value,
  onChange,
  side
}: {
  value: string;
  onChange: (value: string) => void;
  side: InstitutionTypeSide;
}) {
  const { data: institutionTypes } = useInstitutionTypes();
  const options = useMemo(
    () => toInstitutionTypeOptions(institutionTypes, side),
    [institutionTypes, side]
  );

  /*
    Nothing at all until the options arrive, rather than an empty control. The
    step is already complete without this field — `other` is the value — so a
    selector that cannot yet offer an alternative is strictly worse than the step
    the reader had before it existed.
  */
  if (options.length === 0) return null;

  return (
    <InlineCreateSelectField
      label="Type"
      value={value}
      options={options}
      onChange={onChange}
      hint="What this organisation holds. Sets the letters and forms Vale uses for it."
    />
  );
}
